///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2021, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2021 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { FetchError, error400 } from "./FetchError";

function handleFetchError(response: Response): Promise<Response> {
  if (!response.ok) {
    switch (response.status) {
      case 400: {
        return response.text().then((text) => {
          console.error(text);
          return Promise.reject(new FetchError(400, error400(text)));
        });
      }
      case 500: {
        return response.text().then((text) => {
          console.error(error400(text, text));
          return Promise.reject(new FetchError(500));
        });
      }
      default:
        return Promise.reject(new FetchError(response.status));
    }
  }
  return Promise.resolve(response);
}

export function $fetch(
  url: string,
  params: {
    method: "GET" | "POST" | "PUT" | "DELETE";
    headers?: HeadersInit;
    body?: BodyInit | object;
    signal?: AbortSignal;
  } = { method: "GET" }
): Promise<Response> {
  const headers = { ...params.headers };
  delete headers["Content-Type"];

  let body: FormData | string | undefined = undefined;
  if (params.method === "POST" || params.method === "PUT") {
    if (params.body instanceof FormData) {
      body = params.body;
    } else if (params.body instanceof Blob) {
      body = new FormData();
      body.append("file", params.body);
    } else if (params.body instanceof ArrayBuffer) {
      body = new FormData();
      body.append("file", new Blob([params.body]));
    } else if (typeof params.body === "object") {
      body = JSON.stringify(params.body);
      headers["Content-Type"] = "application/json";
    } else if (typeof params.body === "string") {
      body = params.body;
      headers["Content-Type"] = "text/plain";
    }
  }

  const init: RequestInit = { method: params.method, headers };
  if (body) init.body = body;
  if (params.signal) init.signal = params.signal;

  return fetch(url, init).then(handleFetchError);
}
